import { Button, DatePicker, Drawer, Input, Select } from 'antd';
import dayjs from 'dayjs';
import sortBy from 'lodash/sortBy';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DATE_BACKEND_FORMAT, DAY_FORMAT } from '../../../constants';
import { getPartners } from '../../../modules/partners/api';

const { RangePicker } = DatePicker;

const OrdersFilter = ({ open, onClose, query, onApply, riders, hidePartner }) => {
  const { t } = useTranslation('orders');
  const { t: tc } = useTranslation('common');
  const [partnerOptions, setPartnerOptions] = useState([]);

  // Local filter state
  const [name, setName] = useState('');
  const [routeId, setRouteId] = useState('');
  const [partnerOrderRef, setPartnerOrderRef] = useState('');
  const [orderRef, setOrderRef] = useState('');
  const [status, setStatus] = useState(undefined);
  const [dateRange, setDateRange] = useState(null);
  const [riderId, setRiderId] = useState(undefined);
  const [partnerId, setPartnerId] = useState(undefined);
  const [orderType, setOrderType] = useState(undefined);
  const [issueTypes, setIssueTypes] = useState([]);

  const filterOption = (input, option = null) => (option?.label ?? '').toLowerCase()?.includes(input.toLowerCase());

  useEffect(() => {
    if (hidePartner) return;
    const loadPartners = async () => {
      const { response } = await getPartners({ take: 200 });
      if (response?.partners) {
        setPartnerOptions(response.partners.map((p) => ({ label: p.name, value: String(p.id) })));
      }
    };
    loadPartners();
  }, [hidePartner]);

  // Sync local state from query when drawer opens
  useEffect(() => {
    if (open) {
      setName(query.name || '');
      setRouteId(query.routeId || '');
      setPartnerOrderRef(query.partnerOrderRef || '');
      setOrderRef(query.orderRef || '');
      setStatus(query.status || undefined);
      setDateRange(
        query.deliveryDateFrom || query.deliveryDateTo
          ? [
              query.deliveryDateFrom ? dayjs(query.deliveryDateFrom, DATE_BACKEND_FORMAT) : null,
              query.deliveryDateTo ? dayjs(query.deliveryDateTo, DATE_BACKEND_FORMAT) : null,
            ]
          : null,
      );
      setRiderId(query.riderId ? Number(query.riderId) : undefined);
      setPartnerId(query.partnerId || undefined);
      setOrderType(query.orderType || undefined);
      setIssueTypes(query.issueTypes ? query.issueTypes.split(',') : []);
    }
  }, [open, query]);

  const handleApply = () => {
    const params = { ...query };
    params.name = name || undefined;
    params.routeId = routeId || undefined;
    params.partnerOrderRef = partnerOrderRef || undefined;
    params.orderRef = orderRef || undefined;
    params.status = status || undefined;
    params.deliveryDateFrom = dateRange && dateRange[0] ? dateRange[0].format(DATE_BACKEND_FORMAT) : undefined;
    params.deliveryDateTo = dateRange && dateRange[1] ? dateRange[1].format(DATE_BACKEND_FORMAT) : undefined;
    params.riderId = riderId || undefined;
    params.partnerId = partnerId || undefined;
    params.orderType = orderType || undefined;
    params.issueTypes = Array.isArray(issueTypes) && issueTypes.length > 0 ? issueTypes.join(',') : undefined;
    params.current = 1;
    onApply(params, true);
    onClose();
  };

  const handleClear = () => {
    setName('');
    setRouteId('');
    setPartnerOrderRef('');
    setOrderRef('');
    setStatus(undefined);
    setDateRange(null);
    setRiderId(undefined);
    setPartnerId(undefined);
    setOrderType(undefined);
    setIssueTypes([]);
    onApply({ current: 1 }, true);
    onClose();
  };

  return (
    <Drawer
      title={t('filterOrders')}
      placement="right"
      onClose={onClose}
      open={open}
      zIndex={1010}
      width={360}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={handleClear}>{tc('clear')}</Button>
          <Button type="primary" onClick={handleApply}>
            {tc('apply')}
          </Button>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>{tc('name')}</label>
          <Input placeholder={t('orderName')} value={name} onChange={(e) => setName(e.target.value)} allowClear />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>{t('routeId')}</label>
          <Input placeholder={t('routeId')} value={routeId} onChange={(e) => setRouteId(e.target.value)} allowClear />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>
            {t('partnerOrderRefFilter')}
            <span style={{ fontWeight: 400, color: '#999', fontSize: 12, marginLeft: 4 }}>
              {t('partnerOrderRefFilterHint')}
            </span>
          </label>
          <Input
            placeholder={t('partnerOrderRefFilter')}
            value={partnerOrderRef}
            onChange={(e) => setPartnerOrderRef(e.target.value)}
            allowClear
          />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>
            {t('orderRef')}
            <span style={{ fontWeight: 400, color: '#999', fontSize: 12, marginLeft: 4 }}>
              {t('orderRefFilterHint')}
            </span>
          </label>
          <Input
            placeholder={t('orderRef')}
            value={orderRef}
            onChange={(e) => setOrderRef(e.target.value)}
            allowClear
          />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>{tc('status')}</label>
          <Select
            style={{ width: '100%' }}
            placeholder={t('allStatuses')}
            value={status}
            onChange={(val) => setStatus(val)}
            allowClear
            options={Object.keys(tc('orderStatuses', { returnObjects: true })).map((key) => ({
              label: tc(`orderStatuses.${key}`),
              value: key,
            }))}
          />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>{t('deliveryDate')}</label>
          <RangePicker
            style={{ width: '100%' }}
            value={dateRange}
            onChange={(dates) => setDateRange(dates)}
            format={DAY_FORMAT}
            placeholder={[tc('from'), tc('to')]}
            allowEmpty={[true, true]}
          />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>{tc('type')}</label>
          <Select
            style={{ width: '100%' }}
            placeholder={t('allFleets')}
            value={riderId}
            onChange={(val) => setRiderId(val)}
            allowClear
            showSearch
            filterOption={filterOption}
            options={sortBy(riders || [], (r) => r.user.name).map((r) => ({
              label: r.user.name,
              value: r.id,
            }))}
          />
        </div>
        {!hidePartner && (
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>{t('partner')}</label>
            <Select
              style={{ width: '100%' }}
              placeholder={t('allPartners')}
              value={partnerId}
              onChange={(val) => setPartnerId(val)}
              allowClear
              showSearch
              filterOption={filterOption}
              options={partnerOptions}
            />
          </div>
        )}
        <div>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>{t('orderType')}</label>
          <Select
            style={{ width: '100%' }}
            placeholder={t('allTypes')}
            value={orderType}
            onChange={(val) => setOrderType(val)}
            allowClear
            options={Object.keys(tc('orderTypes', { returnObjects: true })).map((key) => ({
              label: tc(`orderTypes.${key}`),
              value: key,
            }))}
          />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>{t('issueType')}</label>
          <Select
            style={{ width: '100%' }}
            placeholder={t('selectIssueTypes')}
            value={issueTypes}
            onChange={(val) => setIssueTypes(val)}
            mode="multiple"
            allowClear
            showSearch
            filterOption={filterOption}
            options={Object.keys(tc('issueTypes', { returnObjects: true })).map((key) => ({
              value: key,
              label: tc(`issueTypes.${key}`),
            }))}
          />
        </div>
      </div>
    </Drawer>
  );
};

export default OrdersFilter;
